import { useEffect, useRef, useState, useCallback } from 'react';
import type { FC } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import type { Truck, Driver, Task, Zone } from '../types';
import { getCachedRoute, type RouteCoordinate } from '../services/routing';
import './Map.css';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface MapProps {
  trucks: Truck[];
  drivers: Driver[];
  selectedTruck: Truck | null;
  route: { latitude: number; longitude: number }[];
  onTruckSelect: (truck: Truck | null) => void;
  sidebarOpen?: boolean;
  sidebarExpanded?: boolean;
  zoomTrigger?: number;
  zones?: Zone[];
  tasks?: Task[];
  selectedTask?: Task | null;
  onTaskSelect?: (task: Task | null) => void;
}

// Store for real routes
interface TaskRoute {
  taskId: string;
  coordinates: RouteCoordinate[];
  distance: number;
  duration: number;
}

// Calculate size based on zoom level
const getSizeForZoom = (zoom: number, isSelected: boolean): number => {
  // Base size ranges from 16px at zoom 5 to 48px at zoom 18
  const minZoom = 5;
  const maxZoom = 18;
  const minSize = 16;
  const maxSize = isSelected ? 52 : 44;
  
  const clampedZoom = Math.max(minZoom, Math.min(maxZoom, zoom));
  const ratio = (clampedZoom - minZoom) / (maxZoom - minZoom);
  return Math.round(minSize + ratio * (maxSize - minSize));
};

// Custom truck icon with proper truck SVG

// --- Icon cache to prevent DOM recreation and animation restart ---
// When Leaflet gets a new icon object, it replaces the marker DOM, restarting
// any CSS animations. By caching icons by their visual key, we return the same
// instance when nothing changed, so Leaflet keeps the existing DOM untouched.
const truckIconCache = new Map<string, L.DivIcon>();
const driverIconCache = new Map<string, L.DivIcon>();
const pickupIconCache = new Map<string, L.DivIcon>();
const dropoffIconCache = new Map<string, L.DivIcon>();

// Custom driver marker icon
const createDriverIcon = (driver: Driver, zoom: number) => {
  const cacheKey = `${driver.isTracking}-${zoom}`;
  const cached = driverIconCache.get(cacheKey);
  if (cached) return cached;

  const color = driver.isTracking ? '#3b82f6' : '#6b7280';
  const size = getSizeForZoom(zoom, false) - 4;
  
  const personSvg = `
    <svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
  `;
  
  const icon = L.divIcon({
    className: 'custom-driver-marker',
    html: `
      <div class="driver-marker ${driver.isTracking ? 'tracking' : ''}">
        <div class="driver-marker-inner" style="background: ${color}; width: ${size}px; height: ${size}px;">
          ${personSvg}
        </div>
        ${driver.isTracking ? '<div class="driver-pulse"></div>' : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });

  driverIconCache.set(cacheKey, icon);
  return icon;
};

const createTruckIcon = (status: string, _heading: number, isSelected: boolean, zoom: number) => {
  const cacheKey = `${status}-${isSelected}-${zoom}`;
  const cached = truckIconCache.get(cacheKey);
  if (cached) return cached;

  // Orange color for trucks (matching driver marker style)
  const color = '#f59e0b';
  const size = getSizeForZoom(zoom, isSelected) - 4;
  
  // Truck SVG icon
  const truckSvg = `
    <svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
    </svg>
  `;
  
  const icon = L.divIcon({
    className: 'custom-truck-marker',
    html: `
      <div class="truck-marker ${status} ${isSelected ? 'selected' : ''}">
        <div class="truck-marker-inner" style="background: ${color}; width: ${size}px; height: ${size}px;">
          ${truckSvg}
        </div>
        ${status === 'moving' ? '<div class="truck-pulse"></div>' : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });

  truckIconCache.set(cacheKey, icon);
  return icon;
};

// Component to track zoom level and handle double-click zoom to max
const ZoomTracker: FC<{ onZoomChange: (zoom: number) => void; selectedTruck: Truck | null }> = ({ onZoomChange, selectedTruck }) => {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
    },
    dblclick: () => {
      // Double-click zooms to max level - center on selected truck if available
      if (selectedTruck) {
        map.flyTo([selectedTruck.latitude, selectedTruck.longitude], 18, {
          duration: 1.2,
        });
      }
    },
  });
  
  // Set initial zoom
  useEffect(() => {
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);
  
  return null;
};

// Component to handle map centering and resizing
const MapController: FC<{ selectedTruck: Truck | null; trucks: Truck[]; sidebarOpen?: boolean; sidebarExpanded?: boolean; zoomTrigger?: number }> = ({ selectedTruck, trucks, sidebarOpen, sidebarExpanded = true, zoomTrigger }) => {
  const map = useMap();
  const initializedRef = useRef(false);
  const selectedTruckRef = useRef<Truck | null>(null);
  
  // Keep ref updated with latest selectedTruck
  selectedTruckRef.current = selectedTruck;

  // Handle sidebar toggle - invalidate size after animation
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 300); // Match transition duration
    return () => clearTimeout(timer);
  }, [sidebarOpen, sidebarExpanded, map]);

  // Fly to truck ONLY when zoomTrigger changes (user clicked on truck card or marker)
  useEffect(() => {
    const truck = selectedTruckRef.current;
    if (truck && zoomTrigger && zoomTrigger > 0) {
      // Offset center to account for sidebar (closed 0, narrow 56px, expanded 56+380px)
      const sidebarWidth = sidebarOpen ? (sidebarExpanded ? 436 : 56) : 0;
      const offsetX = -sidebarWidth / 2;
      
      // Get current center in pixels, apply offset, convert back to latlng
      const targetLatLng = L.latLng(truck.latitude, truck.longitude);
      const targetPoint = map.project(targetLatLng, 18);
      const offsetPoint = L.point(targetPoint.x - offsetX, targetPoint.y);
      const offsetLatLng = map.unproject(offsetPoint, 18);
      
      map.flyTo(offsetLatLng, 18, {
        duration: 1.2,
      });
    }
  }, [zoomTrigger, map]);

  // Initial centering - only on first load
  useEffect(() => {
    if (trucks.length > 0 && !initializedRef.current) {
      if (trucks.length === 1) {
        // Single truck - center on it at max zoom
        map.setView([trucks[0].latitude, trucks[0].longitude], 18);
      } else {
        // Multiple trucks - fit bounds to show all
        const bounds = L.latLngBounds(trucks.map(t => [t.latitude, t.longitude]));
        map.fitBounds(bounds, { padding: [50, 50] });
      }
      initializedRef.current = true;
    }
  }, [trucks, map]);

  return null;
};

// Create task marker icons (cached)
const createPickupIcon = (isActive: boolean) => {
  const cacheKey = `${isActive}`;
  const cached = pickupIconCache.get(cacheKey);
  if (cached) return cached;

  const size = 24;
  const color = '#4ade80';
  
  const icon = L.divIcon({
    className: 'custom-task-marker',
    html: `
      <div class="task-marker pickup ${isActive ? 'active' : ''}">
        <svg viewBox="0 0 24 24" fill="${color}" width="${size}" height="${size}">
          <circle cx="12" cy="12" r="10" fill="${color}" opacity="0.2"/>
          <circle cx="12" cy="12" r="6" fill="${color}"/>
        </svg>
        ${isActive ? '<div class="task-pulse pickup"></div>' : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

  pickupIconCache.set(cacheKey, icon);
  return icon;
};

const createDropoffIcon = (isActive: boolean) => {
  const cacheKey = `${isActive}`;
  const cached = dropoffIconCache.get(cacheKey);
  if (cached) return cached;

  const size = 28;
  const color = '#ef4444';
  
  const icon = L.divIcon({
    className: 'custom-task-marker',
    html: `
      <div class="task-marker dropoff ${isActive ? 'active' : ''}">
        <svg viewBox="0 0 24 24" fill="none" width="${size}" height="${size}">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill="${color}" opacity="0.2" stroke="${color}" stroke-width="1"/>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill="${color}" stroke="${color}" stroke-width="2"/>
          <circle cx="12" cy="10" r="3" fill="white"/>
        </svg>
        ${isActive ? '<div class="task-pulse dropoff"></div>' : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });

  dropoffIconCache.set(cacheKey, icon);
  return icon;
};

const FleetMap: FC<MapProps> = ({ trucks, drivers, selectedTruck, route, onTruckSelect, sidebarOpen, sidebarExpanded = true, zoomTrigger, zones = [], tasks = [], selectedTask, onTaskSelect }) => {
  // Default center: Sweden (Stockholm area based on your RUT956 location)
  const defaultCenter: [number, number] = [59.349329, 17.996375];
  const [zoomLevel, setZoomLevel] = useState(13);
  const [taskRoutes, setTaskRoutes] = useState<Map<string, TaskRoute>>(new Map());
  const [loadingRoutes, setLoadingRoutes] = useState<Set<string>>(new Set());
  
  // Route line with gradient effect
  const routePositions: [number, number][] = route.map(p => [p.latitude, p.longitude]);
  
  // Active tasks (not completed/cancelled)
  const activeTasks = tasks.filter(t => !['completed', 'failed', 'cancelled'].includes(t.status));
  
  // Fetch real routes for active tasks
  const fetchTaskRoute = useCallback(async (task: Task) => {
    const taskId = task.id;
    
    // Skip if already loading or already have route
    if (loadingRoutes.has(taskId) || taskRoutes.has(taskId)) {
      return;
    }
    
    setLoadingRoutes(prev => new Set(prev).add(taskId));
    
    try {
      const result = await getCachedRoute(
        { latitude: task.pickup.latitude, longitude: task.pickup.longitude },
        { latitude: task.dropoff.latitude, longitude: task.dropoff.longitude },
        'driving'
      );
      
      if (result.success && result.coordinates.length > 0) {
        setTaskRoutes(prev => {
          const newMap = new Map(prev);
          newMap.set(taskId, {
            taskId,
            coordinates: result.coordinates,
            distance: result.distance,
            duration: result.duration,
          });
          return newMap;
        });
      }
    } catch (error) {
      console.error('Error fetching route for task:', taskId, error);
    } finally {
      setLoadingRoutes(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  }, [loadingRoutes, taskRoutes]);
  
  // Fetch routes for all active tasks
  useEffect(() => {
    activeTasks.forEach(task => {
      fetchTaskRoute(task);
    });
  }, [activeTasks.map(t => t.id).join(',')]); // Only re-run when task IDs change
  
  // Get zone color by type
  const getZoneColor = (zone: Zone) => {
    if (zone.color) return zone.color;
    const colors: Record<string, string> = {
      receiving: '#3b82f6',
      shipping: '#f59e0b',
      storage: '#4ade80',
      staging: '#00D26A',
      charging: '#ef4444',
      corridor: '#6b7280',
    };
    return colors[zone.type] || '#4ade80';
  };

  // Get route positions for a task
  const getTaskRoutePositions = (task: Task): [number, number][] => {
    const cachedRoute = taskRoutes.get(task.id);
    if (cachedRoute && cachedRoute.coordinates.length > 0) {
      return cachedRoute.coordinates.map(c => [c.latitude, c.longitude]);
    }
    // Fallback to straight line
    return [
      [task.pickup.latitude, task.pickup.longitude],
      [task.dropoff.latitude, task.dropoff.longitude],
    ];
  };

  return (
    <div className="map-container">
      <MapContainer
        center={defaultCenter}
        zoom={13}
        className="leaflet-map"
        zoomControl={true}
        doubleClickZoom={false}
      >
        {/* Dark map tiles - CartoDB Dark Matter */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        
        <MapController selectedTruck={selectedTruck} trucks={trucks} sidebarOpen={sidebarOpen} sidebarExpanded={sidebarExpanded} zoomTrigger={zoomTrigger} />
        <ZoomTracker onZoomChange={setZoomLevel} selectedTruck={selectedTruck} />
        
        {/* Zone markers */}
        {zones.map(zone => (
          <Circle
            key={zone.id}
            center={[zone.latitude, zone.longitude]}
            radius={30}
            pathOptions={{
              color: getZoneColor(zone),
              fillColor: getZoneColor(zone),
              fillOpacity: 0.15,
              weight: 2,
              dashArray: zone.type === 'corridor' ? '5, 5' : undefined,
            }}
          >
            <Popup>
              <div className="zone-popup">
                <h3>{zone.name}</h3>
                <p className="zone-code font-mono">{zone.code}</p>
                <span className="zone-type">{zone.type}</span>
              </div>
            </Popup>
          </Circle>
        ))}

        {/* Task route lines - REAL ROADS! */}
        {activeTasks.map(task => {
          const isSelected = selectedTask?.id === task.id;
          const routeColor = task.priority === 'urgent' ? '#ef4444' : 
                            task.priority === 'high' ? '#f59e0b' : '#4ade80';
          const taskRoutePositions = getTaskRoutePositions(task);
          const hasRealRoute = taskRoutes.has(task.id);
          
          return (
            <React.Fragment key={`task-route-${task.id}`}>
              {/* Shadow line for depth */}
              <Polyline
                positions={taskRoutePositions}
                pathOptions={{
                  color: '#000',
                  weight: isSelected ? 10 : 6,
                  opacity: 0.4,
                }}
              />
              {/* Main route line */}
              <Polyline
                positions={taskRoutePositions}
                pathOptions={{
                  color: routeColor,
                  weight: isSelected ? 6 : 4,
                  opacity: isSelected ? 1 : 0.8,
                  lineCap: 'round',
                  lineJoin: 'round',
                }}
              />
              {/* Animated dash overlay */}
              <Polyline
                positions={taskRoutePositions}
                pathOptions={{
                  color: '#ffffff',
                  weight: 2,
                  opacity: 0.4,
                  dashArray: hasRealRoute ? '8, 16' : '10, 10',
                  className: 'animated-route',
                }}
              />
            </React.Fragment>
          );
        })}

        {/* Task pickup markers */}
        {activeTasks.map(task => {
          const isActive = selectedTask?.id === task.id || 
                          (task.currentStep === 'navigate_pickup' || task.currentStep === 'at_pickup');
          const routeInfo = taskRoutes.get(task.id);
          
          return (
            <Marker
              key={`pickup-${task.id}`}
              position={[task.pickup.latitude, task.pickup.longitude]}
              icon={createPickupIcon(isActive)}
              eventHandlers={{
                click: () => onTaskSelect?.(task),
              }}
            >
              <Popup>
                <div className="task-popup pickup">
                  <span className="task-label">📍 Upphämtning</span>
                  <h4>{task.pickup.label}</h4>
                  <p>{task.type} · {task.priority}</p>
                  {routeInfo && (
                    <p className="route-info">
                      🛣️ {(routeInfo.distance / 1000).toFixed(1)} km · 
                      ⏱️ {Math.ceil(routeInfo.duration / 60)} min
                    </p>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Task dropoff markers */}
        {activeTasks.map(task => {
          const isActive = selectedTask?.id === task.id || 
                          (task.currentStep === 'navigate_dropoff' || task.currentStep === 'at_dropoff');
          
          return (
            <Marker
              key={`dropoff-${task.id}`}
              position={[task.dropoff.latitude, task.dropoff.longitude]}
              icon={createDropoffIcon(isActive)}
              eventHandlers={{
                click: () => onTaskSelect?.(task),
              }}
            >
              <Popup>
                <div className="task-popup dropoff">
                  <span className="task-label">🎯 Leverans</span>
                  <h4>{task.dropoff.label}</h4>
                  <p>{task.type} · {task.priority}</p>
                </div>
              </Popup>
            </Marker>
          );
        })}
        
        {/* Route polyline */}
        {routePositions.length > 1 && (
          <>
            {/* Shadow line */}
            <Polyline
              positions={routePositions}
              pathOptions={{
                color: '#000',
                weight: 8,
                opacity: 0.3,
              }}
            />
            {/* Main route line */}
            <Polyline
              positions={routePositions}
              pathOptions={{
                color: '#4ade80',
                weight: 4,
                opacity: 0.8,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
            {/* Animated dash line */}
            <Polyline
              positions={routePositions}
              pathOptions={{
                color: '#86efac',
                weight: 2,
                opacity: 0.5,
                dashArray: '10, 20',
                className: 'animated-route',
              }}
            />
          </>
        )}
        
        {/* Truck markers - filter out invalid coordinates */}
        {trucks
          .filter(truck => truck.latitude != null && truck.longitude != null && !isNaN(truck.latitude) && !isNaN(truck.longitude))
          .map(truck => (
          <Marker
            key={truck.id}
            position={[truck.latitude, truck.longitude]}
            icon={createTruckIcon(truck.status, truck.heading, selectedTruck?.id === truck.id, zoomLevel)}
            eventHandlers={{
              click: () => onTruckSelect(truck),
            }}
          >
            <Popup>
              <div className="truck-popup">
                <h3>{truck.name}</h3>
                {truck.plateNumber && <p className="plate">{truck.plateNumber}</p>}
                <div className="popup-stats">
                  <div className="stat">
                    <span className="label">Hastighet</span>
                    <span className="value">{Math.round(truck.speed)} km/h</span>
                  </div>
                  <div className="stat">
                    <span className="label">Status</span>
                    <span className={`value status-${truck.status}`}>
                      {truck.status === 'moving' ? 'I rörelse' : truck.status === 'idle' ? 'Stilla' : 'Offline'}
                    </span>
                  </div>
                </div>
                <button 
                  className="popup-btn"
                  onClick={() => onTruckSelect(truck)}
                >
                  Visa detaljer
                </button>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Driver markers - filter out invalid coordinates */}
        {drivers
          .filter(driver => driver.latitude != null && driver.longitude != null && !isNaN(driver.latitude) && !isNaN(driver.longitude))
          .map(driver => (
          <Marker
            key={driver.id}
            position={[driver.latitude, driver.longitude]}
            icon={createDriverIcon(driver, zoomLevel)}
          >
            <Popup>
              <div className="driver-popup">
                <h3>📱 {driver.name}</h3>
                <p className="driver-status">{driver.isTracking ? 'Spårar' : 'Inaktiv'}</p>
                <div className="popup-stats">
                  <div className="stat">
                    <span className="label">Hastighet</span>
                    <span className="value">{Math.round(driver.speed)} km/h</span>
                  </div>
                  {driver.batteryLevel !== null && (
                    <div className="stat">
                      <span className="label">Batteri</span>
                      <span className="value">{driver.batteryLevel}%</span>
                    </div>
                  )}
                </div>
                {driver.assignedTruck && (
                  <p className="driver-truck">
                    Tilldelad: {trucks.find(t => t.id === driver.assignedTruck)?.name || driver.assignedTruck}
                  </p>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      
      {/* Map overlay info */}
      <div className="map-overlay">
        <div className="map-legend">
          <div className="legend-item">
            <span className="legend-dot moving"></span>
            <span>I rörelse</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot idle"></span>
            <span>Stillastående</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot offline"></span>
            <span>Offline</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot driver"></span>
            <span>Förare</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Need to import React for JSX.Fragment
import React from 'react';

export default FleetMap;
